import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { useNavigate } from 'react-router';
import { usePublishBottomBar } from '../../hooks/useBottomBar';
import { useOrderedBoards } from '../../hooks/useBoards';
import { useNow } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { haptic } from '../../native/platform';
import { boardCards, ofBoard, openCards, useBoardCards, useCards } from '../../store/cards';
import { useSession } from '../../store/session';
import { useSettings } from '../../store/settings';
import { useUi } from '../../store/ui';
import type { BoardInfo } from '../../sync/api';
import { BoardTabs, usePickBoard } from '../Boards/BoardTabs';
import { Check, ChevronUp } from '../icons';
import styles from './ColumnBar.module.css';

const HOLD_MS = 260;
const SLIDE_MIN = 10;
const MANAGE = 'manage';
const HINT_KEY = 'hmawiw-chip-hint';

/**
 * Phone only: the columns and, with more than one board, the boards, in two rows under the
 * thumb. Each column shows how many cards it holds; a tap or a slide along the row goes there.
 */
export function ColumnStrip({ onGo }: { onGo(index: number, record?: boolean): void }) {
  const t = useT();
  const several = useOrderedBoards().length > 1;
  const ref = useRef<HTMLElement>(null);
  usePublishBottomBar(ref);

  return (
    <nav ref={ref} className={styles.bar} aria-label={t.t('nav.board')}>
      <Columns onGo={onGo} />
      {several ? <Boards /> : null}
    </nav>
  );
}

function Columns({ onGo }: { onGo(index: number, record?: boolean): void }) {
  const t = useT();
  const column = useUi((s) => s.column);
  const cards = useBoardCards();
  const now = useNow();
  const columnCount = useSettings((s) => s.columnCount);
  const slide = useRef<{ id: number; x: number; on: boolean; moved: boolean } | null>(null);
  const counts = useMemo(() => {
    const n = Array.from({ length: columnCount }, () => 0);
    for (const c of boardCards(cards, now)) if (c.motivation < columnCount) n[c.motivation] = (n[c.motivation] ?? 0) + 1;
    return n;
  }, [cards, now, columnCount]);

  // The column nearest the finger, so a slide that drifts into a gap or past the ends still counts.
  const columnAt = (row: HTMLElement, x: number): number | null => {
    let best: number | null = null;
    let distance = Infinity;
    for (const seg of row.querySelectorAll<HTMLElement>('[data-seg]')) {
      const r = seg.getBoundingClientRect();
      const d = x < r.left ? r.left - x : x > r.right ? x - r.right : 0;
      if (d < distance) {
        distance = d;
        best = Number(seg.dataset.seg);
      }
    }
    return best;
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const s = slide.current;
    if (!s || s.id !== e.pointerId) return;
    if (!s.on) {
      if (Math.abs(e.clientX - s.x) < SLIDE_MIN) return;
      s.on = true;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // capture refused; the slide follows while the finger stays on the row
      }
    }
    const i = columnAt(e.currentTarget, e.clientX);
    if (i === null || i === useUi.getState().column) return;
    // Back returns to where the slide began, not to each column it crossed.
    onGo(i, !s.moved);
    s.moved = true;
  };

  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    if (slide.current?.id === e.pointerId) slide.current = null;
  };

  return (
    <div
      className={`${styles.segs}${columnCount > 3 ? ` ${styles.compact}` : ''}`}
      role="group"
      aria-label={t.t('nav.columns')}
      data-tour="columnbar"
      onPointerDown={(e) => {
        if (e.button === 0) slide.current = { id: e.pointerId, x: e.clientX, on: false, moved: false };
      }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      {counts.map((n, i) => (
        <button
          key={i}
          type="button"
          className={`${styles.segment}${i === column ? ` ${styles.on}` : ''}`}
          style={{ '--c': `var(--m${i})` } as CSSProperties}
          aria-current={i === column ? 'true' : undefined}
          aria-label={`${t.column(i)}, ${t.tn('count.cards', n)}`}
          data-seg={i}
          onClick={() => onGo(i)}
        >
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.sname}>{t.column(i)}</span>
          <span className={styles.scount}>{n}</span>
        </button>
      ))}
    </div>
  );
}

/** One tap per board while they fit; past that, the board's name and its list. */
function Boards() {
  const boards = useOrderedBoards();
  const cards = useCards((s) => s.cards);
  const pick = usePickBoard(boards);
  const count = (id: string) => openCards(ofBoard(cards, id)).length;
  return (
    <div className={styles.boards}>
      <BoardTabs size="bar" fallback={<BoardChip boards={boards} count={count} pick={pick} />} />
    </div>
  );
}

interface Press {
  id: number;
  x: number;
  y: number;
  scrub: boolean;
  wasOpen: boolean;
}

/**
 * The board's name across the row. A tap lists the boards right above it; a hold, or a slide
 * that starts on it, lets the thumb pick one in a single movement and choose it on release.
 */
export function BoardChip({ boards, count, pick }: { boards: BoardInfo[]; count(id: string): number; pick(id: string): void }) {
  const t = useT();
  const navigate = useNavigate();
  const boardId = useSession((s) => s.boardId);
  const showToast = useUi((s) => s.showToast);
  const open = useUi((s) => s.boardMenu);
  const setOpen = useUi((s) => s.setBoardMenu);
  const [hot, setHot] = useState<string | null>(null);
  const hotRef = useRef<string | null>(null);
  const press = useRef<Press | null>(null);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const focusFirst = useRef(false);
  const pressedScrim = useRef(false);
  const chipRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const current = boards.find((b) => b.id === boardId);

  // Holding and sliding cannot be seen, so it is said once, the first time the name shows.
  useEffect(() => {
    try {
      if (!window.matchMedia('(pointer: coarse)').matches || localStorage.getItem(HINT_KEY)) return;
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      return;
    }
    showToast(t.t('boards.hint'), undefined, 7000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      clearTimeout(hold.current);
      useUi.getState().setBoardMenu(false);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const { toast, hideToast } = useUi.getState();
    if (toast?.message === t.t('boards.hint')) hideToast(toast.id);
    if (focusFirst.current) {
      focusFirst.current = false;
      menuRef.current?.querySelector<HTMLElement>('[aria-checked="true"], [data-menu-item]')?.focus();
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      clearTimeout(hold.current);
      setOpen(false);
      chipRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setOpen]);

  const mark = (key: string | null) => {
    hotRef.current = key;
    setHot(key);
  };

  const close = () => {
    clearTimeout(hold.current);
    mark(null);
    setOpen(false);
  };

  const choose = (key: string) => {
    close();
    if (key === MANAGE) void navigate('/settings#boards');
    else pick(key);
  };

  const scrub = () => {
    const p = press.current;
    if (!p || p.scrub) return;
    clearTimeout(hold.current);
    p.scrub = true;
    setOpen(true);
    haptic('tick');
  };

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // capture refused; the slide still works while the finger stays on the name
    }
    press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, scrub: false, wasOpen: open };
    clearTimeout(hold.current);
    hold.current = setTimeout(scrub, HOLD_MS);
  };

  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (!p || e.pointerId !== p.id) return;
    if (!p.scrub && Math.hypot(e.clientX - p.x, e.clientY - p.y) > SLIDE_MIN) scrub();
    if (!p.scrub) return;
    const item = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>('[data-menu-item]');
    const key = item?.dataset.menuItem ?? null;
    if (key === hotRef.current) return;
    mark(key);
    if (key) haptic('tick');
  };

  const onPointerEnd = (e: PointerEvent<HTMLButtonElement>, cancelled: boolean) => {
    const p = press.current;
    if (!p || e.pointerId !== p.id) return;
    clearTimeout(hold.current);
    press.current = null;
    if (cancelled) return;
    if (p.scrub) {
      // Let go away from the list: it stays open for a plain tap.
      const key = hotRef.current;
      mark(null);
      if (key) choose(key);
    } else if (p.wasOpen) {
      close();
    } else {
      setOpen(true);
    }
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[data-menu-item]') ?? []);
    const at = items.indexOf(document.activeElement as HTMLElement);
    items[(at + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
  };

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        className={styles.chip}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t.t('boards.chip', { name: current?.name ?? '' })}
        data-board-chip=""
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => onPointerEnd(e, false)}
        onPointerCancel={(e) => onPointerEnd(e, true)}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => {
          // Pointers are handled above; this is Enter or Space.
          if (e.detail !== 0) return;
          if (open) {
            close();
          } else {
            focusFirst.current = true;
            setOpen(true);
          }
        }}
      >
        <span className={`display ${styles.chipName}`}>{current?.name ?? ''}</span>
        <span className={styles.chipCount}>{t.tn('count.boards', boards.length)}</span>
        <ChevronUp size={14} />
      </button>
      {open ? (
        <>
          <div
            className={styles.scrim}
            aria-hidden="true"
            onPointerDown={(e) => {
              pressedScrim.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
              if (pressedScrim.current && e.target === e.currentTarget) close();
              pressedScrim.current = false;
            }}
          />
          <div ref={menuRef} className={styles.menu} role="menu" aria-label={t.t('boards.title')} onKeyDown={onMenuKey}>
            {boards.map((b) => (
              <button
                key={b.id}
                type="button"
                role="menuitemradio"
                aria-checked={b.id === boardId}
                className={`${styles.item}${hot === b.id ? ` ${styles.hot}` : ''}`}
                data-menu-item={b.id}
                onClick={() => choose(b.id)}
              >
                <span className={styles.itemName}>{b.name}</span>
                <span className={styles.itemCount}>{count(b.id)}</span>
                <span className={styles.check}>{b.id === boardId ? <Check size={18} /> : null}</span>
              </button>
            ))}
            <button
              type="button"
              role="menuitem"
              className={`${styles.item} ${styles.manage}${hot === MANAGE ? ` ${styles.hot}` : ''}`}
              data-menu-item={MANAGE}
              onClick={() => choose(MANAGE)}
            >
              {t.t('boards.manageIn')}
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}
